import { Injectable } from '@angular/core';
import { defer, Observable, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import {
    AuthStatus,
    AvailableIntegrations,
    Contact,
    IntegrationConfig,
    IntegrationUnit,
    ServiceResource,
} from '../../models/IntegrationEntity';
import { HttpErrorResponse } from '@angular/common/http';
import { IntegrationRepository } from '@/core/repositories/integration/integration.repository';
import {
    AutoImportResponseForm,
    CheckUnitMappingResponseForm,
    ImportResponse,
    InvoiceRequestForm,
    InvoiceResponseForm,
    ResourceImportRequestForm,
    ResourceTypes,
} from '@/core/models/integration-types';
import { ReauthError } from '@/core/types/reauth-error';
import { TtlCacheStore } from '@/core/utils/cache/ttl-cache-store';
import { INTEGRATIONS_CACHE_TTL } from '@/core/config/cache-ttl';
import { TileService } from '@/core/services/open-api/tile.service';
import { ApiError, ApiErrorHandlerService } from '@/core/services/api-error-handler.service';

const RATE_LIMIT_ERROR_CODE = 'INT-004';

type PreviousAuthStatus = Exclude<AuthStatus, 'rate-limited'> | undefined;

interface ActiveRateLimit {
    previousAuthStatus: PreviousAuthStatus;
    error: HttpErrorResponse;
    resetTimer: ReturnType<typeof setTimeout>;
}

/**
 * @class IntegrationService
 * @description Service for managing and interacting with integrations.
 */
@Injectable({ providedIn: 'root' })
export class IntegrationService {
    private readonly integrationsStore = new TtlCacheStore<IntegrationConfig[]>(
        'integrations',
        [],
        INTEGRATIONS_CACHE_TTL,
        // Preserve the backend-provided retry window across integration cache reloads.
        () =>
            this.integrationRepository
                .getAllWithAuthInfo()
                .pipe(map((integrations) => this.applyActiveRateLimits(integrations))),
    );
    private readonly activeRateLimits = new Map<string, ActiveRateLimit>();
    /**
     * The integrations$-observable emits the current list of integrations.
     * It holds information on all integrations that are available to the user, active or not.
     * The first subscription to this observable will trigger a load of the integrations from the server.
     * To force a refresh, call loadIntegrations(true).
     * @returns Observable<IntegrationConfig[]> - The list of integrations.
     */
    public integrations$ = this.integrationsStore.state$;

    constructor(
        private integrationRepository: IntegrationRepository,
        private tileService: TileService,
        private apiErrorHandler: ApiErrorHandlerService,
    ) {}

    /**
     * Loads the integrations from the server and updates the integrations$-observable.
     * The results are cached, so calling this method multiple times in a given time period will not trigger multiple requests.
     * Prefer using integrations$-observable instead of calling this method directly unless you want to force a reload.
     * To force a refresh, call this method with forceLoad=true.
     * @param forceLoad - Forces a reload from the server and omits the cache, when set to true.
     * @returns Observable<IntegrationConfig[]> - The list of integrations.
     */
    public loadIntegrations(forceLoad: boolean = false): Observable<IntegrationConfig[]> {
        return this.integrationsStore.load(forceLoad);
    }

    public clearCache(): void {
        this.clearRateLimitState();
        this.integrationsStore.reset();
    }

    /**
     * Clears active rate-limit timers and state.
     * @returns void
     */
    public clearRateLimitState(): void {
        for (const rateLimit of this.activeRateLimits.values()) {
            clearTimeout(rateLimit.resetTimer);
        }
        this.activeRateLimits.clear();
    }

    /**
     * Refreshes the cache and updates the integrations$ observable
     */
    private refreshCache(): void {
        this.loadIntegrations(true).subscribe();
    }

    /**
     * Configures (creates) a new integration.
     * @param intConfig
     */
    public create(intConfig: IntegrationConfig): Observable<IntegrationConfig> {
        return this.integrationRepository.create(intConfig).pipe(
            tap(() => this.refreshCache()),
            catchError((error) => this.handleError(error)),
        );
    }

    /**
     * Updates an existing integration configuration.
     * @param intConfig
     */
    public update(intConfig: IntegrationConfig): Observable<IntegrationConfig> {
        return this.integrationRepository.update(intConfig).pipe(
            tap(() => this.refreshCache()),
            catchError((error) => this.handleError(error)),
        );
    }

    /**
     * Patches the configuration of an existing integration.
     * @param service The integration service identifier, e.g., sevdesk
     * @param intConfig The integration configuration to update. Only set fields will be updated
     */
    public patchConfig(service: AvailableIntegrations, intConfig: IntegrationConfig): Observable<IntegrationConfig> {
        return this.integrationRepository.patch(service, intConfig).pipe(
            tap(() => this.refreshCache()),
            catchError((error) => this.handleError(error)),
        );
    }

    /**
     * Deletes an integration configuration. This will set back an active integration to available.
     * @param service The integration service identifier, e.g., sevdesk
     */
    public delete(service: AvailableIntegrations): Observable<void> {
        return this.integrationRepository.deleteByService(service).pipe(
            tap(() => {
                this.refreshCache();
                // invalidate the tile cache, so that external refs are updated correctly
                this.tileService.invalidateCache();
            }),
            catchError((error) => this.handleError(error)),
        );
    }

    /**
     * Fetches a list of contacts from the accounting integration service that can be used for invoice creation.
     * @param service The integration service identifier, e.g., sevdesk
     */
    public getCustomersForInvoice(service: string): Observable<Contact[]> {
        return this.getResources(service, ResourceTypes.CONTACTS);
    }

    /**
     * Fetches resources from the accounting integration service.
     * The resources have to be of the supported resources type, see integration properties.
     * @param service The integration service identifier, e.g., sevdesk
     * @param resourceType The type of the resources to fetch, e.g., contacts
     */
    public getResources(service: string, resourceType: ResourceTypes): Observable<ServiceResource[]> {
        return this.executeProviderRequest(service, () =>
            this.integrationRepository.getResources(service, resourceType),
        );
    }

    /**
     * Checks if the unit mapping is configured for all tiles with external refs
     *
     * @param service The integration service identifier, e.g., sevdesk
     * @param tileIds The tiles to be checked
     * @returns An observable that emits a boolean indicating if the unit mapping is configured for all tiles with external refs.
     */
    public checkUnitMappingUsingTiles(service: string, tileIds: number[]): Observable<CheckUnitMappingResponseForm> {
        return this.executeProviderRequest(service, () =>
            this.integrationRepository.checkUnitMapping(service, { tileIds }),
        );
    }

    /**
     * See #checkUnitMappingUsingTiles
     * This method performs the same check, but loads the tiles for the provided activities and layerIndex.
     *
     * @param service The integration service identifier, e.g., sevdesk
     * @param layerIndex The layer index to check the tiles for
     * @param activityIds The activities to check the tiles for
     * @returns An observable that emits a boolean indicating if the unit mapping is configured for all tiles with external refs.
     */
    public checkUnitMappingUsingActivities(
        service: string,
        layerIndex: number,
        activityIds: number[],
    ): Observable<CheckUnitMappingResponseForm> {
        return this.executeProviderRequest(service, () =>
            this.integrationRepository.checkUnitMapping(service, { activities: { layerIndex, activityIds } }),
        );
    }

    /**
     * Fetches units from the accounting integration service.
     * @param service The integration service identifier, e.g., sevdesk
     */
    public getUnits(service: string): Observable<IntegrationUnit[]> {
        return this.executeProviderRequest(service, () => this.integrationRepository.getUnits(service));
    }

    /**
     * Creates an invoice in the accounting integration service.
     * @param service The integration service identifier, e.g., sevdesk
     * @param invoiceRequest The invoice request object containing all information to build the invoice: filters for time entries, contact id, hourly rate, etc.
     * @returns Observable<InvoiceResponseForm> - The invoice response object containing some invoice information and a link to the invoice in the accounting services system.
     */
    public createInvoice(service: string, invoiceRequest: InvoiceRequestForm): Observable<InvoiceResponseForm> {
        return this.executeProviderRequest(service, () =>
            this.integrationRepository.createInvoice(service, invoiceRequest),
        );
    }

    /**
     * Imports resources from the accounting integrations system as tiles into the specified layer.
     * @param service The integration service identifier, e.g., sevdesk
     * @param importRequest The import request object containing the layer id and the resources to import.
     * @returns Observable<ImportResponse> - The import response object containing the number of imported resources and the number of failed resources.
     */
    public importResources(service: string, importRequest: ResourceImportRequestForm): Observable<ImportResponse> {
        return this.executeProviderRequest(service, () =>
            this.integrationRepository.importResources(service, importRequest),
        );
    }

    /**
     * This method enables or disables auto import for the specified resource type and layer.
     * If auto import is enabled, resources will be imported automatically on creation in the accounting integration system.
     * @param service The integration service identifier, e.g., sevdesk
     * @param enable Flag to enable or disable auto import for the resource type and layer.
     * @param layerId The layer id to import the resources into.
     * @param resourceType The resource type defining the resources to import into the specified layer, e.g., contacts.
     */
    public configureAutoImport(
        service: string,
        enable: boolean,
        layerId: number,
        resourceType: ResourceTypes,
    ): Observable<AutoImportResponseForm> {
        return this.executeProviderRequest(service, () =>
            this.integrationRepository.configureAutoImport(service, enable, layerId, resourceType),
        );
    }

    /**
     * This method initiates the OAuth2 authorization flow for the specified service.
     * The user will be redirected to the OAuth2 provider to authorize the app.
     * After the authorization at the accounting integration auth provider, the user will get redirected to
     * the backend (callback url) and finally to the frontend.
     * @param service
     */
    public initOauth2(service: string): Observable<{ url: string }> {
        return this.integrationRepository
            .getOAuth2InitUrl(service)
            .pipe(catchError((error) => this.handleError(error)));
    }

    public register(service: string, params: Record<string, string>): Observable<IntegrationConfig> {
        return this.integrationRepository.register(service, params).pipe(
            tap(() => this.refreshCache()),
            catchError((error) => this.handleError(error)),
        );
    }

    /**
     * Executes a provider request unless the integration is currently rate-limited.
     * @param service The integration service identifier.
     * @param request A function that creates the provider request.
     * @returns An observable that emits the provider response or the active rate-limit error.
     */
    private executeProviderRequest<T>(service: string, request: () => Observable<T>): Observable<T> {
        return defer(() => {
            const activeRateLimit = this.activeRateLimits.get(service);
            if (activeRateLimit) {
                // No request is sent, so the HTTP error interceptor never runs and would never show
                // its toast. Without this the blocked call fails with nothing shown to the user (#738).
                this.notifyRateLimited(activeRateLimit.error);
                return throwError(() => activeRateLimit.error);
            }

            return request().pipe(catchError((error) => this.handleError(error, service)));
        });
    }

    /** Shows the notification the HTTP error interceptor would have shown for this response. */
    private notifyRateLimited(error: HttpErrorResponse): void {
        const apiError = error.error?.error as ApiError | undefined;
        if (apiError?.code !== undefined) {
            this.apiErrorHandler.handleError({ ...apiError, httpCode: error.status });
        }
    }

    private handleError(error: HttpErrorResponse, service?: string): Observable<never> {
        if (service && this.isRateLimitError(error)) {
            this.markRateLimited(service, error);
            return throwError(() => error);
        }
        if (this.isReauthError(error)) {
            return throwError(() => new ReauthError(error));
        } else {
            console.error('IntegrationService - error', error);
        }
        return throwError(() => error);
    }

    /**
     * Marks an integration as rate-limited when the backend provides a valid retry time.
     * @param service The integration service identifier.
     * @param error The backend rate-limit error.
     * @returns void
     */
    private markRateLimited(service: string, error: HttpErrorResponse): void {
        const resetDelay = this.getRateLimitResetDelay(error);
        if (resetDelay === undefined) {
            return;
        }

        const existingRateLimit = this.activeRateLimits.get(service);
        if (existingRateLimit) {
            clearTimeout(existingRateLimit.resetTimer);
        }

        const previousAuthStatus = existingRateLimit?.previousAuthStatus ?? this.getCurrentAuthStatus(service);
        const resetTimer = setTimeout(() => {
            const currentRateLimit = this.activeRateLimits.get(service);
            if (currentRateLimit?.resetTimer !== resetTimer) {
                return;
            }
            this.activeRateLimits.delete(service);
            this.updateIntegrationAuthStatus(service, previousAuthStatus);
        }, resetDelay);

        this.activeRateLimits.set(service, {
            previousAuthStatus,
            error,
            resetTimer,
        });
        this.updateIntegrationAuthStatus(service, 'rate-limited');
    }

    /**
     * Calculates the delay until the backend retry time.
     * @param error The backend rate-limit error.
     * @returns The delay in milliseconds, or undefined when the retry time cannot be used.
     */
    private getRateLimitResetDelay(error: HttpErrorResponse): number | undefined {
        const retryAfter = error.error?.error?.details?.retryAfter;
        if (typeof retryAfter !== 'string') {
            return undefined;
        }

        const retryAt = new Date(retryAfter).getTime();
        if (Number.isNaN(retryAt) || retryAt <= Date.now()) {
            return undefined;
        }

        return retryAt - Date.now();
    }

    /**
     * Gets the status that should be restored after the rate limit expires.
     * @param service The integration service identifier.
     * @returns The current non-rate-limited auth status.
     */
    private getCurrentAuthStatus(service: string): PreviousAuthStatus {
        const authStatus = this.integrationsStore
            .get()
            .find((integration) => integration.service === service)?.authStatus;
        return authStatus === 'rate-limited' ? undefined : authStatus;
    }

    /**
     * Updates the cached auth status without refreshing it from the backend.
     * @param service The integration service identifier.
     * @param authStatus The auth status to store.
     * @returns void
     */
    private updateIntegrationAuthStatus(service: string, authStatus: AuthStatus | undefined): void {
        const integrations = this.integrationsStore.get();
        let changed = false;
        const updatedIntegrations = integrations.map((integration) => {
            if (integration.service !== service || integration.authStatus === authStatus) {
                return integration;
            }
            changed = true;
            return { ...integration, authStatus };
        });

        if (changed) {
            // Preserve the existing cache expiry: this is a local status update, not fresh server data.
            this.integrationsStore.set(updatedIntegrations, false);
        }
    }

    /**
     * Preserves locally active rate-limit statuses when integrations are reloaded.
     * @param integrations Integrations returned by the backend.
     * @returns Integrations with active rate limits applied.
     */
    private applyActiveRateLimits(integrations: IntegrationConfig[]): IntegrationConfig[] {
        return integrations.map(
            (integration): IntegrationConfig =>
                this.activeRateLimits.has(integration.service)
                    ? { ...integration, authStatus: 'rate-limited' }
                    : integration,
        );
    }

    /**
     * Checks whether a backend error represents an integration rate limit.
     * @param error The backend error to check.
     * @returns True when the error code represents an integration rate limit.
     */
    private isRateLimitError(error: HttpErrorResponse): boolean {
        return error.error?.error?.code === RATE_LIMIT_ERROR_CODE;
    }

    /**
     * Determines if an error indicates the user needs to reauthenticate
     * (currently only used for Lexware integration)
     * @param httpError The error object (typically from HTTP request)
     * @returns boolean - true if the error indicates reauthentication is required
     */
    private isReauthError(httpError: HttpErrorResponse): boolean {
        const errorCode = httpError.error?.error?.code;
        if (errorCode === 'INT-002' || errorCode === 'INT-005') {
            return true;
        }
        return [400, 401].includes(httpError.status) && /reauthenticate|reauth/i.test(httpError.error?.message || '');
    }
}
